import { motion } from 'framer-motion';
import { CheckCircle, AlertCircle, ExternalLink, Users, MapPin, Clock, Mail } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '../../ui/Card';
import { Badge } from '../../ui/Badge';

import { 
  PROJECT_CATEGORIES, 
  FUNDING_FOR_OPTIONS,
  CAMPAIGN_DURATION,
  getCurrencySymbol 
} from '../../../constants/index.js';

const ReviewSubmitStep = ({ formData, errors }) => {
  // Helper functions
  const getFundingForDisplay = () => {
    if (formData.funding_for === FUNDING_FOR_OPTIONS.OTHERS && formData.funding_for_other) {
      return formData.funding_for_other;
    }
    
    const labels = {
      [FUNDING_FOR_OPTIONS.PERSONAL]: 'Personal',
      [FUNDING_FOR_OPTIONS.APP_DEVELOPMENT]: 'App Development'
    };
    
    return labels[formData.funding_for] || formData.funding_for;
  };

  const getCategoryDisplay = () => {
    const categoryLabels = {
      [PROJECT_CATEGORIES.MEDICAL]: 'Medical Relief',
      [PROJECT_CATEGORIES.EDUCATION]: 'Education',
      [PROJECT_CATEGORIES.TECHNOLOGY]: 'Technology',
      [PROJECT_CATEGORIES.ENVIRONMENT]: 'Environment',
      [PROJECT_CATEGORIES.COMMUNITY]: 'Community',
      [PROJECT_CATEGORIES.ARTS]: 'Arts & Culture'
    };
    
    return categoryLabels[formData.category] || formData.category;
  };

  const getDurationDisplay = () => {
    const durationLabels = {
      [CAMPAIGN_DURATION.THREE_DAYS]: '3 Days',
      [CAMPAIGN_DURATION.SEVEN_DAYS]: '7 Days',
      [CAMPAIGN_DURATION.TWO_WEEKS]: '2 Weeks',
      [CAMPAIGN_DURATION.FOUR_WEEKS]: '4 Weeks',
      [CAMPAIGN_DURATION.SIX_WEEKS]: '6 Weeks',
      [CAMPAIGN_DURATION.THREE_MONTHS]: '3 Months'
    };
    
    return durationLabels[formData.campaign_duration] || formData.campaign_duration;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const isFormComplete = () => {
    const requiredFields = [
      'title', 'funding_for', 'category', 'story', 'funding_goal',
      'campaign_duration', 'country', 'contact_email', 'cover_image'
    ];

    const missingFields = requiredFields.filter(field => {
      const value = formData[field];
      return !value || value === '';
    });

    // Check conditional fields
    if (formData.funding_for === FUNDING_FOR_OPTIONS.OTHERS && !formData.funding_for_other) {
      missingFields.push('funding_for_other');
    }

    if (formData.is_app_listing && !formData.app_url) {
      missingFields.push('app_url');
    }

    // Check funding breakdown
    if (!formData.funding_use || formData.funding_use.length === 0) {
      missingFields.push('funding_use');
    }

    return {
      complete: missingFields.length === 0 && Object.keys(errors).length === 0,
      missingFields
    };
  };

  const completionStatus = isFormComplete();

  return (
    <div className="space-y-6 max-w-full overflow-hidden px-1 sm:px-0">
      {/* Header */}
      <Card className="border-0 shadow-soft">
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="text-xl sm:text-h4 font-heading text-neutral-900 flex items-center gap-2">
            ✅ Review & Submit
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 pt-0 sm:pt-0">
          <p className="text-sm sm:text-p2-r text-neutral-600">
            Review all your project information before submitting. Once submitted, your project will be reviewed and you'll be notified of the status.
          </p>
        </CardContent>
      </Card>

      {/* Completion Status */}
      <Card className={`border-2 ${completionStatus.complete ? 'bg-green-50 border-green-200' : 'bg-orange-50 border-orange-200 text-orange-900'}`}>
        <CardContent className="p-4 sm:p-6">
          <div className="flex items-start gap-3">
            {completionStatus.complete ? (
              <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
            ) : (
              <AlertCircle className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
            )}
            <div className={`text-sm sm:text-p2-r ${completionStatus.complete ? 'text-green-800' : 'text-orange-800'}`}>
              <p className="font-bold mb-1 text-base">
                {completionStatus.complete ? 'Ready to Submit!' : 'Action Required'}
              </p>
              {completionStatus.complete ? (
                <p>Your project is complete and ready for submission.</p>
              ) : (
                <div className="mt-2">
                  <p className="mb-2 font-medium">Please complete the following required fields:</p>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
                    {completionStatus.missingFields.map(field => (
                      <li key={field} className="flex items-center gap-1.5 text-xs sm:text-sm">
                        <div className="w-1 h-1 bg-orange-400 rounded-full"></div>
                        {field.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Project Summary */}
      <Card className="border-0 shadow-soft">
        <CardHeader>
          <CardTitle className="text-p1-m text-neutral-900">Project Summary</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="space-y-5">
              <h3 className="text-base sm:text-p1-m font-bold text-neutral-900 pb-2 border-b border-neutral-100 flex items-center gap-2">
                📝 Basic Information
              </h3>
              <div className="space-y-4">
                <div>
                  <p className="text-xs sm:text-p2-r text-neutral-500 mb-0.5">Title</p>
                  <p className="text-sm sm:text-p1-r font-medium text-neutral-900">{formData.title || 'Not provided'}</p>
                </div>
                <div>
                  <p className="text-xs sm:text-p2-r text-neutral-500 mb-1">Funding For & Category</p>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="outline" className="bg-neutral-50">{getFundingForDisplay()}</Badge>
                    <Badge className="bg-primary-teal/10 text-primary-teal border-primary-teal/20">{getCategoryDisplay()}</Badge>
                  </div>
                </div>
                {formData.is_app_listing && (
                  <div>
                    <p className="text-xs sm:text-p2-r text-neutral-500 mb-0.5">App URL</p>
                    <a 
                      href={formData.app_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary-teal hover:underline flex items-center gap-1.5 text-sm font-medium"
                    >
                      Visit Application
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-5">
              <h3 className="text-base sm:text-p1-m font-bold text-neutral-900 pb-2 border-b border-neutral-100 flex items-center gap-2">
                💰 Financial Details
              </h3>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs sm:text-p2-r text-neutral-500 mb-0.5">Currency</p>
                    <p className="text-sm font-semibold text-neutral-900">
                      {formData.currency}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs sm:text-p2-r text-neutral-500 mb-0.5">Duration</p>
                    <div className="flex items-center gap-1.5 text-sm font-semibold text-neutral-900">
                      <Clock className="w-3.5 h-3.5 text-neutral-400" />
                      {getDurationDisplay()}
                    </div>
                  </div>
                </div>
                <div>
                  <p className="text-xs sm:text-p2-r text-neutral-500 mb-0.5">Funding Goal</p>
                  <p className="text-2xl sm:text-3xl font-bold text-primary-teal tracking-tight">
                    {getCurrencySymbol(formData.currency)}{formatCurrency(formData.funding_goal || 0)}
                  </p>
                </div>
                <div>
                  <p className="text-xs sm:text-p2-r text-neutral-500 mb-0.5">Location</p>
                  <div className="flex items-center gap-1.5 text-sm font-medium text-neutral-700">
                    <MapPin className="w-3.5 h-3.5 text-neutral-400" />
                    {formData.country}{formData.region ? `, ${formData.region}` : ''}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Story Preview */}
          <div>
            <h3 className="text-base sm:text-p1-m font-bold text-neutral-900 mb-3 flex items-center gap-2">
              📖 Project Story
            </h3>
            <div className="bg-neutral-50/50 p-4 rounded-xl border border-neutral-100">
              <p className="text-sm sm:text-p2-r text-neutral-700 leading-relaxed italic">
                {formData.story ? 
                  (formData.story.length > 300 ? 
                    `${formData.story.substring(0, 300)}...` : 
                    formData.story
                  ) : 
                  'No story provided'
                }
              </p>
              {formData.story && (
                <div className="mt-3 text-right">
                  <span className="text-[10px] uppercase tracking-wider font-bold text-neutral-400">
                    {formData.story.length} Characters
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Funding Breakdown */}
          {formData.funding_use && formData.funding_use.length > 0 && (
            <div>
              <h3 className="text-p1-m text-neutral-900 mb-3 flex items-center gap-2">
                💰 Funding Breakdown
              </h3>
              <div className="space-y-3">
                {formData.funding_use.map((item, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg">
                    <div className="flex-1">
                      <p className="text-p2-m text-neutral-900">{item.category}</p>
                      <p className="text-p2-r text-neutral-600">{item.description}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-p2-m text-neutral-900">{getCurrencySymbol(formData.currency)} {formatCurrency(item.amount)}</p>
                      <p className="text-p2-r text-neutral-600">{item.percentage}%</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Team Members */}
          {formData.is_app_listing && formData.team_members && formData.team_members.length > 0 && (
            <div>
              <h3 className="text-p1-m text-neutral-900 mb-3 flex items-center gap-2">
                👥 Team Members
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {formData.team_members.map((member, index) => (
                  <div key={index} className="p-3 bg-neutral-50 rounded-lg">
                    <p className="text-p2-m text-neutral-900">{member.name}</p>
                    <p className="text-p2-r text-neutral-600">{member.email}</p>
                    {member.social_link && (
                      <a 
                        href={member.social_link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-primary-teal hover:underline text-p2-r flex items-center gap-1 mt-1"
                      >
                        Social Profile
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Milestones */}
          {formData.milestones && formData.milestones.length > 0 && (
            <div>
              <h3 className="text-p1-m text-neutral-900 mb-3 flex items-center gap-2">
                🎯 Project Milestones
              </h3>
              <div className="space-y-3">
                {formData.milestones.map((milestone, index) => (
                  <div key={index} className="p-3 bg-neutral-50 rounded-lg">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="text-p2-m text-neutral-900">{milestone.title}</p>
                        <p className="text-p2-r text-neutral-600">{milestone.description}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-p2-r text-neutral-600">
                          {new Date(milestone.date).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Media Preview */}
          <div>
            <h3 className="text-base sm:text-p1-m font-bold text-neutral-900 mb-4 flex items-center gap-2">
              📷 Project Media
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {/* Cover Image */}
              {formData.cover_image && (
                <div className="relative group">
                  <img
                    src={formData.cover_image}
                    alt="Cover"
                    className="w-full aspect-video sm:aspect-square object-cover rounded-xl shadow-sm"
                  />
                  <div className="absolute top-2 left-2">
                    <Badge className="bg-primary-teal text-[10px] px-1.5 py-0.5">Cover</Badge>
                  </div>
                </div>
              )}

              {/* Video Preview Indicator */}
              {formData.video_url && (
                <div className="relative group bg-neutral-900 rounded-xl overflow-hidden aspect-video sm:aspect-square flex flex-col items-center justify-center border border-neutral-800">
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center mb-2">
                    <div className="w-0 h-0 border-t-[6px] border-t-transparent border-l-[10px] border-l-white border-b-[6px] border-b-transparent ml-1"></div>
                  </div>
                  <span className="text-[10px] font-bold text-white uppercase tracking-wider">Video Attached</span>
                  <div className="absolute top-2 left-2">
                    <Badge variant="outline" className="bg-white/10 text-white border-white/20 text-[10px] px-1.5 py-0.5">Video</Badge>
                  </div>
                </div>
              )}
              
              {/* Gallery Images */}
              {(formData.gallery_images || []).map((imageUrl, index) => (
                <div key={index} className="relative group">
                  <img
                    src={imageUrl}
                    alt={`Gallery ${index + 1}`}
                    className="w-full aspect-video sm:aspect-square object-cover rounded-xl shadow-sm"
                  />
                  <div className="absolute top-2 left-2">
                    <Badge variant="secondary" className="bg-white/80 backdrop-blur-sm text-neutral-700 text-[10px] px-1.5 py-0.5 border-neutral-200">
                      #{index + 1}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Contact Information */}
          <div>
            <h3 className="text-p1-m text-neutral-900 mb-3 flex items-center gap-2">
              📧 Contact Information
            </h3>
            <div className="p-3 bg-neutral-50 rounded-lg">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-neutral-500" />
                <span className="text-p1-r text-neutral-900">{formData.contact_email}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Terms and Conditions */}
      <Card className="border-0 shadow-soft">
        <CardHeader>
          <CardTitle className="text-p1-m text-neutral-900">Terms & Conditions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-neutral-50 p-4 rounded-lg">
            <h4 className="text-p2-m text-neutral-900 mb-3">By submitting this project, you agree to:</h4>
            <ul className="space-y-2 text-p2-r text-neutral-700">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                Provide accurate and truthful information about your project
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                Use funds raised exclusively for the stated project purposes
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                Provide regular updates to donors on project progress
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                Comply with CROWBLOCK's terms of service and community guidelines
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                Accept the 1.99% platform commission on all donations received
              </li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Submission Info */}
      <Card className="bg-primary-teal/10 border-primary-teal/20 border-2">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-primary-teal mt-0.5 flex-shrink-0" />
            <div className="text-p2-r text-primary-teal">
              <p className="font-semibold mb-1">What happens next?</p>
              <ul className="space-y-1 text-sm">
                <li>• Your project will be submitted for review</li>
                <li>• Our team will review it within 48 hours</li>
                <li>• You'll receive an email notification with the decision</li>
                <li>• If approved, your project will go live immediately</li>
                <li>• You'll get a shareable link to promote your project</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ReviewSubmitStep;
